import type { ProgramScopedContentRow, ProgramScopedContentRowUpsert } from '../types/domain';
import type { TrackProgramMode } from './trackProgramModeStorage';
import {
  mergeProgramLearningContentFields,
  readProgramLearningContentFields,
  writeProgramLearningContentFields,
} from './programLearningContentFields';

/** 동기화 편제 fan-out 대상(평가열까지). 수업기록·멤버그룹은 alias별 독립 */
export const TRACK_SYNC_FANOUT_FIELD_KEYS = [
  'unit_text',
  'learning_text',
  'learning_note',
  'presentation_ref_id',
  'worksheet_ref_id',
  'page_text',
  'assistant_page_text',
  'current_period_text',
  'total_period_text',
  'material_text',
  'record_cross_curricular_text',
  'record_memo_text',
  'record_evaluation_text',
] as const;

export function isSyncTrackMirrorScope(
  templateLabel: string,
  scopeLookupKey: string,
  preferredTrackScopeKey: string,
  trackProgramModeByTrack: Record<string, TrackProgramMode>
): boolean {
  const tl = templateLabel.trim();
  if (!tl || !scopeLookupKey || !preferredTrackScopeKey) return false;
  if (scopeLookupKey === preferredTrackScopeKey) return false;
  return (trackProgramModeByTrack[tl] ?? 'unified') === 'sync';
}

function mergeFanoutBgColor(
  srcBg: Record<string, string> | null | undefined,
  peerBg: Record<string, string> | null | undefined
): Record<string, string> {
  const next = { ...(srcBg ?? {}) };
  delete next.record_class_text;
  const peerRecordBg = peerBg?.record_class_text;
  if (peerRecordBg) next.record_class_text = peerRecordBg;
  return next;
}

/** 대표 scope 저장 → 미러 alias scope fan-out upsert(수업기록은 peer 유지) */
export function buildTrackSyncFanoutScopedUpsert(
  src: ProgramScopedContentRow | ProgramScopedContentRowUpsert,
  targetKey: string,
  orderIndex: number,
  sourceAnnualCellKey: string | null,
  existingPeer?: ProgramScopedContentRow | null
): ProgramScopedContentRowUpsert {
  return {
    id: existingPeer?.id,
    workspace_id: src.workspace_id,
    grade_id: src.grade_id,
    academic_year: src.academic_year,
    term_number: src.term_number,
    scope_type: 'track',
    scope_key: targetKey,
    order_index: orderIndex,
    source_annual_cell_key: sourceAnnualCellKey,
    timetable_text: null,
    unit_text: src.unit_text ?? null,
    learning_text: src.learning_text ?? null,
    learning_note: src.learning_note ?? '',
    presentation_ref_id: src.presentation_ref_id ?? null,
    worksheet_ref_id: src.worksheet_ref_id ?? null,
    page_text: src.page_text ?? null,
    assistant_page_text: src.assistant_page_text ?? null,
    current_period_text: src.current_period_text ?? null,
    total_period_text: src.total_period_text ?? null,
    material_text: src.material_text ?? null,
    record_cross_curricular_text: src.record_cross_curricular_text ?? null,
    record_memo_text: src.record_memo_text ?? null,
    record_evaluation_text: src.record_evaluation_text ?? null,
    record_class_text: existingPeer?.record_class_text ?? null,
    record_member_group_id: existingPeer?.record_member_group_id ?? null,
    bg_color_by_field: mergeFanoutBgColor(src.bg_color_by_field, existingPeer?.bg_color_by_field),
  };
}

/** 미러 scope fan-out 시 수업기록 필드는 peer(또는 빈 값) 유지 */
export function preserveIndependentScopedRecordFieldsOnFanout<
  T extends {
    record_class_text?: string | null;
    record_member_group_id?: string | null;
    bg_color_by_field?: Record<string, string>;
  },
>(row: T, existingPeer?: ProgramScopedContentRow | null): T {
  if (!existingPeer) {
    return {
      ...row,
      record_class_text: '',
      record_member_group_id: null,
      bg_color_by_field: mergeFanoutBgColor(row.bg_color_by_field, undefined),
    };
  }
  return {
    ...row,
    record_class_text: existingPeer.record_class_text ?? '',
    record_member_group_id: existingPeer.record_member_group_id ?? null,
    bg_color_by_field: mergeFanoutBgColor(row.bg_color_by_field, existingPeer.bg_color_by_field),
  };
}

function sortScopedRowsByOrder(rows: ProgramScopedContentRow[]): ProgramScopedContentRow[] {
  return [...rows].sort((a, b) => a.order_index - b.order_index);
}

/** 동기화 미러 표: 대표 scoped 행의 fan-out 필드를 행 순서대로 덮어씀(수업기록은 미러 유지) */
export function overlaySyncMirrorProgramContentFromRepresentative(
  mirrorRows: ProgramScopedContentRow[],
  representativeRows: ProgramScopedContentRow[]
): ProgramScopedContentRow[] {
  if (!representativeRows.length) return mirrorRows;
  const repSorted = sortScopedRowsByOrder(representativeRows);
  return sortScopedRowsByOrder(mirrorRows).map((mirrorRow, idx) => {
    const repRow = repSorted[idx];
    if (!repRow) return mirrorRow;
    return {
      ...mirrorRow,
      unit_text: repRow.unit_text ?? '',
      learning_text: repRow.learning_text ?? '',
      learning_note: repRow.learning_note ?? '',
      presentation_ref_id: repRow.presentation_ref_id ?? null,
      worksheet_ref_id: repRow.worksheet_ref_id ?? null,
      page_text: repRow.page_text ?? '',
      assistant_page_text: repRow.assistant_page_text ?? '',
      current_period_text: repRow.current_period_text ?? '',
      total_period_text: repRow.total_period_text ?? '',
      material_text: repRow.material_text ?? '',
      record_cross_curricular_text: repRow.record_cross_curricular_text ?? '',
      record_memo_text: repRow.record_memo_text ?? '',
      record_evaluation_text: repRow.record_evaluation_text ?? '',
      record_class_text: mirrorRow.record_class_text ?? '',
      record_member_group_id: mirrorRow.record_member_group_id ?? null,
      bg_color_by_field: mergeFanoutBgColor(repRow.bg_color_by_field, mirrorRow.bg_color_by_field),
    };
  });
}

/** 대표 워크스페이스 더티 병합 후: DB 대표 행의 presentation/worksheet ref만 보충 */
export function overlaySyncMirrorLearningTextRefsFromRepresentative(
  mirrorRows: ProgramScopedContentRow[],
  representativeRows: ProgramScopedContentRow[]
): ProgramScopedContentRow[] {
  if (!representativeRows.length) return mirrorRows;
  const repSorted = sortScopedRowsByOrder(representativeRows);
  return sortScopedRowsByOrder(mirrorRows).map((mirrorRow, idx) => {
    const repRow = repSorted[idx];
    if (!repRow) return mirrorRow;
    const mirrorFields = readProgramLearningContentFields(mirrorRow);
    const repFields = readProgramLearningContentFields(repRow);
    const mergedFields = mergeProgramLearningContentFields(mirrorFields, {
      body: mirrorFields.body,
      note: mirrorFields.note,
      presentationRefId: repFields.presentationRefId || mirrorFields.presentationRefId,
      worksheetRefId: repFields.worksheetRefId || mirrorFields.worksheetRefId,
    });
    return {
      ...mirrorRow,
      ...writeProgramLearningContentFields(mergedFields),
    };
  });
}
